// La Guarida del Bardo — radio comunitaria por YouTube.
//
// Un GM sintoniza un video de YouTube via /radio <url>. El server broadcast
// el videoId a todos los clientes. Cada jugador toglea personalmente con
// /radio (sin args). Cuando escucha:
//   - Se silencia la música del mapa (musicEngine.setEnabled(false)).
//   - Suena el video de YouTube, sincronizado al timestamp del server
//     (elapsed = now - startedAt → todos oyen aproximadamente lo mismo).
// Cuando toglea off, restaura la música del mapa.
//
// Filosofía:
//   - Cero impacto en el WS del juego (audio streamea directo a los CDNs
//     de YouTube — nuestro server no proxea nada).
//   - Iframe hidden fuera de viewport, tamaño 1×1 (no consume espacio
//     visual, mínimo consumo de rendering).
//   - Toggle personal persistido en localStorage — sobrevive reloads.
//   - Autoplay policy: el toggle se dispara desde chat (Enter en /radio),
//     que cuenta como user interaction → el browser permite autoplay.

let ytApiPromise = null;
function loadYouTubeAPI() {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
        if (typeof window === "undefined") {
            resolve(null);
            return;
        }
        if (window.YT && window.YT.Player) {
            resolve(window.YT);
            return;
        }
        // Callback global que YouTube llama cuando su API está lista.
        // Preservamos cualquier handler previo (defensive).
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = function() {
            if (typeof prev === "function") {
                try { prev(); } catch (e) {}
            }
            resolve(window.YT);
        };
        const s = document.createElement("script");
        s.src = "https://www.youtube.com/iframe_api";
        s.async = true;
        s.onerror = () => {
            console.warn("[radio] failed to load YouTube IFrame API");
            resolve(null);
        };
        document.head.appendChild(s);
    });
    return ytApiPromise;
}

// Cada cuánto verificar drift + re-seek si necesita. 20s es un buen
// balance: raro el user note pequeños ajustes, pero suficiente para
// mantener a todos dentro de 500ms del mismo momento del track.
const RESYNC_INTERVAL_MS = 20000;
// Umbral de drift para disparar seek. Debajo de esto no hacemos seek
// (el user no lo nota + el seek en sí puede crear un micro-glitch).
const RESYNC_THRESHOLD_MS = 500;

class RadioEngine {
    constructor() {
        // v2026-07-02: la radio arranca SIEMPRE OFF al conectarse al char.
        // La persistencia en localStorage se quitó — cada login re-elige
        // (default: escuchar la música del mapa). El toggle solo vive
        // por sesión hasta desconectarse.
        this.enabled = false;
        this.currentVideoId = null;        // last track set by server
        this.currentStartedAtMs = 0;
        this.currentGmName = null;
        this.currentTitle = null;          // título del video (set en onStateChange PLAYING)
        this.player = null;                // YT.Player instance
        this.playerReady = false;
        this.volume = 45;                  // 0-100 (default un poco arriba de music)
        this._resyncTimer = null;          // setInterval id del re-sync loop
    }

    // Corrige drift entre este cliente y el "reloj" del server. YouTube
    // playback drift natural es bajo, pero variaciones de conexión y
    // buffering acumulan diff con el tiempo. Este loop compara la
    // posición REAL del player con la posición ESPERADA (calculada
    // desde startedAt) y hace un seek suave si difieren > umbral.
    _startResyncLoop() {
        if (this._resyncTimer) return;
        this._resyncTimer = setInterval(() => {
            try {
                if (
                    !this.enabled ||
                    !this.player ||
                    !this.playerReady ||
                    !this.currentVideoId ||
                    this._inAutoContinueMode
                ) return;
                const actualSec = this.player.getCurrentTime();
                const expectedSec = (Date.now() - this.currentStartedAtMs) / 1000;
                if (typeof actualSec !== "number" || actualSec <= 0) return;
                const driftMs = Math.abs(actualSec - expectedSec) * 1000;
                if (driftMs > RESYNC_THRESHOLD_MS && expectedSec > 1) {
                    this.player.seekTo(expectedSec, true);
                }
            } catch (e) {}
        }, RESYNC_INTERVAL_MS);
    }

    _stopResyncLoop() {
        if (this._resyncTimer) {
            clearInterval(this._resyncTimer);
            this._resyncTimer = null;
        }
    }

    // Reset explícito al login/re-conexión — garantiza estado limpio
    // incluso cuando el módulo singleton sobrevive a una navegación SPA
    // (que NO recarga el módulo, así que el constructor no vuelve a
    // ejecutarse). Llamado desde messages.js:getMyCharacter.
    resetForLogin() {
        this.enabled = false;
        this._inAutoContinueMode = false;
        this._pause();
        try {
            const musicEngine = require("./music").default;
            if (musicEngine && typeof musicEngine.setEnabled === "function") {
                // Música del mapa vuelve SEGÚN el setting del user — no
                // incondicional (respeta el toggle OFF de settings).
                musicEngine.setEnabled(this._mapMusicSettingEnabled());
            }
        } catch (e) {}
    }

    setVolume(v) {
        this.volume = Math.max(0, Math.min(100, +v || 0));
        if (this.player && this.playerReady) {
            try { this.player.setVolume(this.volume); } catch (e) {}
        }
    }

    // Server nos avisa: hay un nuevo track (o silencio).
    // Si estamos actualmente escuchando, arranca inmediatamente. Si no,
    // solo guarda el estado — al togglear on más tarde, arrancará desde
    // la posición sincronizada actual.
    setCurrentTrack(videoId, startedAtMs, gmName) {
        // Track cambió → invalidar título viejo; el nuevo se seteará
        // cuando el player emita PLAYING con el nuevo videoId.
        if (this.currentVideoId !== videoId) {
            this.currentTitle = null;
        }
        this.currentVideoId = videoId || null;
        this.currentStartedAtMs = startedAtMs || Date.now();
        this.currentGmName = gmName || null;
        // Salir del modo auto-continue si estábamos en él — el server
        // recuperó el control con una track explícita (queue avance o
        // /radio next / GM lo cambió).
        this._inAutoContinueMode = false;
        if (this.enabled) {
            this._playCurrent();
        }
    }

    // Crea el iframe player la primera vez. Idempotente después.
    async _ensurePlayer() {
        if (this.player) return this.player;
        const YT = await loadYouTubeAPI();
        if (!YT) return null;
        return new Promise((resolve) => {
            // Container hidden — 1px fuera del viewport.
            const container = document.createElement("div");
            container.id = "radioBardoContainer";
            container.style.cssText =
                "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;" +
                "overflow:hidden;pointer-events:none;z-index:-1;";
            document.body.appendChild(container);
            const el = document.createElement("div");
            el.id = "radioBardoPlayer";
            container.appendChild(el);

            this.player = new YT.Player("radioBardoPlayer", {
                width: "1",
                height: "1",
                host: "https://www.youtube-nocookie.com",
                playerVars: {
                    controls: 0,
                    disablekb: 1,
                    modestbranding: 1,
                    playsinline: 1,
                    rel: 0,
                    autoplay: 1,
                    origin: window.location.origin,
                    // Sin cards / anotaciones sobre el video — cero rendering
                    // extra ya que el iframe es 1×1 igual, pero YouTube deja
                    // de bajar assets de overlay que no vamos a usar.
                    iv_load_policy: 3,
                    fs: 0
                },
                events: {
                    onReady: () => {
                        this.playerReady = true;
                        try {
                            this.player.setVolume(this.volume);
                            // Best-effort: pedir calidad máxima. YouTube
                            // suele ignorar esta llamada en versiones nuevas
                            // (auto-selección por bandwidth) pero no daña.
                            // El audio bitrate lo elige YouTube y no está
                            // expuesto al API — no podemos forzarlo.
                            if (typeof this.player.setPlaybackQuality === "function") {
                                this.player.setPlaybackQuality("hd1080");
                            }
                        } catch (e) {}
                        resolve(this.player);
                    },
                    onStateChange: (e) => {
                        if (!e) return;
                        // 1 = PLAYING. Momento perfecto para hacer:
                        //   1) Capturar el título (getVideoData() ya es confiable acá).
                        //   2) Aplicar el seek pendiente (sync al servidor). YouTube
                        //      a veces ignora startSeconds en loadVideoById si el video
                        //      no bufferó lo suficiente — el seek en PLAYING sí pega.
                        if (e.data === 1) {
                            try {
                                const data = this.player.getVideoData();
                                if (data && data.title) {
                                    this.currentTitle = data.title;
                                }
                            } catch (err) {}
                            if (typeof this._pendingSeekSec === "number" && this._pendingSeekSec > 1) {
                                try {
                                    this.player.seekTo(this._pendingSeekSec, true);
                                } catch (err) {}
                                this._pendingSeekSec = null;
                            }
                        }
                        // 0 = ENDED. Track terminó — avisamos al server para que
                        // avance la cola (o dispare auto-continue). Solo enviamos
                        // si estamos en modo "single track" (no auto-mix, donde
                        // YouTube maneja las transiciones internamente).
                        if (e.data === 0 && !this._inAutoContinueMode && this.currentVideoId) {
                            try {
                                if (typeof window !== "undefined" && window.__generalRef) {
                                    window.__generalRef.sendRadioTrackEnded(this.currentVideoId);
                                }
                            } catch (err) {}
                        }
                    },
                    onError: (e) => {
                        // Errores comunes de YouTube:
                        //   2   → videoId inválido
                        //   5   → HTML5 player error
                        //   100 → video removido / privado
                        //   101 → embed deshabilitado por el autor
                        //   150 → embed deshabilitado (equivalente a 101)
                        // Los mostramos en el chat del juego para debug visible.
                        const codeMap = {
                            2: "videoId inválido",
                            5: "error del player HTML5",
                            100: "video privado o removido",
                            101: "embed deshabilitado por el autor",
                            150: "embed deshabilitado (política del canal)"
                        };
                        const code = e && e.data;
                        const reason = codeMap[code] || ("código " + code);
                        console.warn("[radio] YT error:", code, reason);
                        try {
                            const game = window.__radioDebugGame;
                            if (game && typeof game.writeConsole === "function") {
                                game.writeConsole(
                                    "♫ YouTube rechazó el video de La Guarida del Bardo: " + reason + ". Probá con otro link.",
                                    "#ff8080"
                                );
                            }
                        } catch (err) {}
                    }
                }
            });
        });
    }

    async _playCurrent() {
        if (!this.currentVideoId) {
            this._pause();
            return;
        }
        await this._ensurePlayer();
        if (!this.player || !this.playerReady) return;
        const elapsedSec = Math.max(
            0,
            (Date.now() - this.currentStartedAtMs) / 1000
        );
        console.log("[radio] sync: elapsed=" + elapsedSec.toFixed(1) + "s (server startedAt=" + this.currentStartedAtMs + ", now=" + Date.now() + ")");
        // Guardamos el target de seek para reusarlo en el listener de
        // PLAYING (el seek "en frío" desde loadVideoById a veces se
        // ignora si el video no bufferó lo suficiente).
        this._pendingSeekSec = elapsedSec;
        try {
            // Cargamos SIN startSeconds — vamos a hacer el seek explícito
            // en el onStateChange=PLAYING para garantizar que se aplique.
            this.player.loadVideoById(this.currentVideoId);
            this.player.setVolume(this.volume);
            setTimeout(() => {
                try {
                    if (this.player && this.playerReady) {
                        this.player.playVideo();
                    }
                } catch (e) {}
            }, 100);
            this._startResyncLoop();
        } catch (e) {
            console.warn("[radio] play error:", e && e.message);
        }
    }

    _pause() {
        this._stopResyncLoop();
        if (this.player && this.playerReady) {
            try { this.player.stopVideo(); } catch (e) {}
        }
    }

    // La Guarida del Bardo — arrancar modo "Mix personalizado".
    // Server nos avisa cuando la cola se vació. Cargamos list=RD<seed>
    // que es la lista Mix de YouTube (radio auto-generada basada en el
    // seed + preferencias del user si está logeado). Playback deja de
    // estar sincronizada — cada cliente sigue su propia recomendación.
    async startAutoContinueMix(seedVideoId) {
        if (!seedVideoId) return;
        this._inAutoContinueMode = true;
        this.currentVideoId = seedVideoId;
        this.currentStartedAtMs = Date.now();
        this.currentTitle = null;
        if (!this.enabled) return; // toggle personal off — no reproducir
        await this._ensurePlayer();
        if (!this.player || !this.playerReady) return;
        try {
            this.player.loadPlaylist({
                list: "RD" + seedVideoId,
                listType: "playlist",
                index: 0
            });
            this.player.setVolume(this.volume);
        } catch (e) {
            console.warn("[radio] autoContinue error:", e && e.message);
        }
    }

    // Lee el setting persistido del toggle de música del mapa. La radio
    // debe RESPETARLO al apagarse: si el user tenía la música OFF en
    // settings, apagar la radio no la revive.
    _mapMusicSettingEnabled() {
        try {
            if (typeof localStorage !== "undefined") {
                return localStorage.getItem("musicEnabled") !== "false";
            }
        } catch (e) {}
        return true;
    }

    // Ídem para el volumen del slider (0-100) — la radio lo adopta al
    // encenderse, así el mismo control maneja ambas fuentes.
    _mapMusicSettingVolume() {
        try {
            if (typeof localStorage !== "undefined") {
                const v = parseInt(localStorage.getItem("musicVolume"), 10);
                if (!isNaN(v)) return Math.max(0, Math.min(100, v));
            }
        } catch (e) {}
        return this.volume;
    }

    // Toggle personal. Retorna el nuevo estado (bool). No persiste
    // (la radio siempre arranca OFF al login — ver constructor).
    async toggleEnabled() {
        this.enabled = !this.enabled;
        if (this.enabled) {
            // Adoptar el volumen del slider de música al encender.
            this.setVolume(this._mapMusicSettingVolume());
        }
        try {
            const musicEngine = require("./music").default;
            if (musicEngine && typeof musicEngine.setEnabled === "function") {
                // Radio ON → mapa OFF. Radio OFF → mapa según el setting
                // del user (antes era `true` incondicional — revivía la
                // música aunque el user la tuviera apagada en settings).
                musicEngine.setEnabled(
                    this.enabled ? false : this._mapMusicSettingEnabled()
                );
            }
        } catch (e) {}
        if (this.enabled) {
            // Si la radio global está en modo Mix (auto-continue), cargar
            // el Mix — _playCurrent haría loadVideoById del seed desde 0,
            // que no es lo que suena "en la radio".
            if (this._inAutoContinueMode && this.currentVideoId) {
                await this.startAutoContinueMix(this.currentVideoId);
            } else {
                await this._playCurrent();
            }
        } else {
            this._pause();
        }
        return this.enabled;
    }

    // Getters para UI.
    isEnabled() { return this.enabled; }
    hasTrack() { return !!this.currentVideoId; }
    getCurrentGmName() { return this.currentGmName; }
    getCurrentTitle() { return this.currentTitle; }
}

const radioEngine = new RadioEngine();
export default radioEngine;
